// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll from "@eisinc/App/Components/Common/FancyScroll";
import FloutingUiMenu from "@eisinc/App/Components/Menu/DropdownMenu";
import MenuItemBadge from "@eisinc/App/Components/MenuItemBadge/MenuItemBadge";
import xHeaderUpdate from "@eisinc/App/Handlers/XNG/Header";
import { DEFAULT_MENU_WIDTH, MAX_DEFAULT_MENU_WIDTH } from "@eisinc/XDL/constants";
import ArrowRight from "@eisinc/XDL/Icons/ArrowRight";
import { xClassNames } from "@eisinc/XDL/Utilities/classNames";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import isMobile from "ismobilejs";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export type MenuVariants = "primary" | "secondary";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    // XNG-Changes
    // iconProps?: IconPresentationProps;
    properties?: {
        categoryBadge?: string;
    };
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
        // XNG-Changes
        parentMenuLink?: StyledProp<ClickablePresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
    // XNG-Changes
    viewAllLink?: LinkPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement<{ id: string; className: string }>;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /** XNG-Changes: To prevent Menu close by click on trigger */
        hideOnClick?: boolean;
        /** XNG-Changes */
        headingLink?: { url: string; title?: string };
        // xng-change
        maxNumberOfLinks?: number;
        /**
         * XNG-Changes
         * Choose 'Primary' when the subMenu needs to match the parent menu height.
         */
        menuVariant?: MenuVariants;
    }
>;

// <XNG-Changes>
const mapDispatchToProps = {
    xHeaderUpdate,
};
// </XNG-Changes>

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps &
    MenuPresentationProps &
    ThemeProps<BaseTheme> &
    // XNG-Changes
    ResolveThunks<typeof mapDispatchToProps>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        // XNG-Changes
        maxNumberOfLinks?: number;
    };

const MenuStyle = styled.div<MenuStyleProps>`
    background: ${getColor("common.background")};
    z-index: ${getProp("theme.zIndex.menu")};
    box-shadow: ${getProp("theme.shadows.2")};
    width: ${getProp("width", 175)}px;
    list-style: none;
    position: absolute;
    margin: 0;
    padding: 0;
    display: none;

    ${injectCss}
`;

type MenuWrapperProps = { canBeOpen?: boolean; isOpen?: true } & InjectableCss;

const MenuWrapper = styled.nav<MenuWrapperProps>`
    position: relative; /* stylelint-disable */
    ${({ isOpen }) =>
        isOpen &&
        `& > ${MenuStyle} {
                display: block;
            }
    `}
    ${({ canBeOpen }) =>
        canBeOpen &&
        `
    &:focus-within > ${MenuStyle}  /* stylelint-enable */ {
        display: block;
    }
    .trigger:focus + ${MenuStyle},
    &:hover > ${MenuStyle},
    &.focus-within > ${MenuStyle} {
        display: block;
    }`}
    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    padding: 10px 15px 10px 20px;
    color: ${getColor("text.main")};
    display: block;
    box-sizing: border-box;
    ${({ hasChildren }: { hasChildren?: boolean }) => hasChildren && "width: calc(100% - 24px);"}
`;

type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        // onClick: () => void;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;
    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }
    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }
    a {
        width: 100%;
        min-height: 42px;
        justify-content: space-between;
        &:hover {
            background: ${getColor("common.border")};
        }
    }
    ${injectCss}
`;

// <XNG-Changes>

const ParentMenuLink = styled(Clickable)<ClickableProps>`
    ${injectCss}
`;

const DELAY = {
    long: 120,
    short: 80,
};

// </XNG-Changes>

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class Menu extends React.Component<MenuProps, MenuState> {
    menuWrapperRef = React.createRef<HTMLElement>();

    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        // width: 175,
        // XNG-Changes
        width: DEFAULT_MENU_WIDTH,
        hideOnClick: false,
    };

    state: MenuState = {
        canBeOpen: true,
        isMobile: false,
        shouldRender: true,
    };

    componentDidMount() {
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // I prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    // menuWrapperMouseLeaveHandler = () => {
    //     if (!this.menuWrapperRef.current) {
    //         return;
    //     }

    //     this.menuWrapperRef.current.blur();

    //     const menuTrigger = this.menuWrapperRef.current.children[0] as HTMLElement;
    //     if (!menuTrigger) {
    //         return;
    //     }

    //     menuTrigger.blur();
    // };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    // XNG-Changes : add headingLink
                    const { menuItems, menuTrigger, isOpen, mergeCss, headingLink, menuVariant, ...otherProps } =
                        this.props;
                    const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;
                    const renderMenuItems = (
                        // XNG-Changes
                        parentDepth: number,
                        childMenuItems: MappedLink[],
                        currentDepth: number,
                        // XNG-Changes
                        parentItem?: MappedLink,
                    ) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const maxDepth = otherProps.maxDepth || 3;
                        // <XNG-Changes>
                        const parentMenu = React.createRef<HTMLDivElement>();
                        const maxNumberOfLinks = otherProps.maxNumberOfLinks || 0;

                        // </XNG-Changes>

                        return (
                            // XNG-Changes : add FancyScroll
                            <FancyScroll
                                refElement={parentMenu}
                                color="common.white"
                                extendedStyles={{
                                    css: css`
                                        width: ${otherProps.width || DEFAULT_MENU_WIDTH}px;
                                        max-width: ${MAX_DEFAULT_MENU_WIDTH}vw;
                                        height: 100%;
                                    `,
                                }}
                                extendToFullWidth={true}
                            >
                                <MenuStyle
                                    width={otherProps.width}
                                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                    // XNG-Changes
                                    ref={parentMenu}
                                    className="MenuStyle"
                                    data-depth={currentDepth}
                                    maxNumberOfLinks={maxNumberOfLinks}
                                >
                                    {/* XNG-Changes */}
                                    {parentItem && (
                                        <ParentMenuLink
                                            href={parentItem.url}
                                            onClick={() => {
                                                this.closeMenu();
                                            }}
                                            target={parentItem.openInNewWindow ? "_blank" : ""}
                                            css={applyStyledProp(["cssOverrides", "parentMenuLink"], resolvedMergeCss)}
                                        >
                                            <StyledWrapper>
                                                {parentItem.title}
                                                <Icon src={ArrowRight} size={16} />
                                            </StyledWrapper>
                                        </ParentMenuLink>
                                    )}
                                    {/* XNG-Changes */}

                                    {childMenuItems
                                        ?.filter(item => !item.excludeFromNavigation)
                                        .map((item, index) => {
                                            // XNG-Changes
                                            const maxDepth = item.maxDepth || parentDepth;

                                            const hasChildren =
                                                item.children?.some(o => !o.excludeFromNavigation) &&
                                                currentDepth < maxDepth;

                                            if (
                                                (item.type === "ChangeCustomerPage" &&
                                                    !this.props.displayChangeCustomerLink) ||
                                                !this.state.shouldRender
                                            ) {
                                                return null;
                                            }

                                            const menuItem = (
                                                <MenuItemStyle
                                                    width={otherProps.width}
                                                    css={applyStyledProp(
                                                        ["cssOverrides", "menuItem"],
                                                        resolvedMergeCss,
                                                    )}
                                                    key={`${item.title}_${index}`}
                                                    data-test-selector="menuItem"
                                                    onClick={() => {
                                                        this.closeMenuWithoutChildren(hasChildren);
                                                    }}
                                                    // XNG-Changes
                                                    className={xClassNames.append([
                                                        "MenuItem",
                                                        hasChildren ? "has-children" : "",
                                                    ])}
                                                    data-depth={currentDepth}
                                                >
                                                    <Clickable
                                                        href={item.url}
                                                        // XNG-Changes
                                                        // onClick={this.closeMenu}
                                                        onClick={() => {
                                                            this.closeMenu();
                                                        }}
                                                        target={item.openInNewWindow ? "_blank" : ""}
                                                        // {...item.clickableProps}
                                                        {...applyProp("menuItemClickableProps")}
                                                    >
                                                        {/* {item.iconProps && <IconMemo {...item.iconProps} />} */}
                                                        {/* XNG-Changes */}
                                                        <MenuItemBadge badgeText={item.properties?.categoryBadge} />

                                                        <MenuItemText
                                                            hasChildren={hasChildren}
                                                            {...applyProp("menuItemTypographyProps")}
                                                            data-test-selector="menuItemText"
                                                        >
                                                            {item.title}
                                                        </MenuItemText>
                                                        {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                                    </Clickable>
                                                    {/* {hasChildren
                                                        ? renderMenuItems(item!.children!, currentDepth + 1)
                                                        : null} */}
                                                </MenuItemStyle>
                                            );
                                            // XNG-Changes
                                            return (
                                                <StyledWrapper key={`${item.title}_${index}`}>
                                                    {!hasChildren && menuItem}
                                                    {hasChildren && (
                                                        <FloutingUiMenu
                                                            key={`FloutingUiMenu_${item.title}_${index}`}
                                                            menuTrigger={menuItem}
                                                            placement="right-start"
                                                            offset={0}
                                                            delay={{ open: 50, close: 500 }} // Stop flickering
                                                            variant={menuVariant}
                                                        >
                                                            {renderMenuItems(
                                                                maxDepth,
                                                                item!.children!,
                                                                currentDepth + 1,
                                                                item,
                                                            )}
                                                        </FloutingUiMenu>
                                                    )}
                                                </StyledWrapper>
                                            );
                                        })}
                                </MenuStyle>
                            </FancyScroll>
                        );
                    };

                    // const cssOverrides = spreadProps("cssOverrides");
                    const descriptionId = otherProps.descriptionId || uniqueId();
                    return (
                        <>
                            {/* XNG-Changes: use FloutingUiMenu */}
                            <FloutingUiMenu
                                menuTrigger={
                                    <MenuWrapper
                                        css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                                        {...{ "aria-labelledby": descriptionId }}
                                    >
                                        {React.cloneElement(menuTrigger, {
                                            id: descriptionId,
                                            //  className: "trigger"
                                            className: `trigger trigger-item`,
                                        })}
                                    </MenuWrapper>
                                }
                                placement="bottom-start"
                                offset={0}
                                delay={{ open: 50, close: 500 }} // Stop flickering
                                variant={menuVariant}
                            >
                                <StyledWrapper
                                    css={css`
                                        width: ${otherProps.width || DEFAULT_MENU_WIDTH}px;
                                        max-width: ${MAX_DEFAULT_MENU_WIDTH}vw;
                                    `}
                                >
                                    {renderMenuItems(otherProps.maxDepth || 0, menuItems, 1)}
                                </StyledWrapper>
                            </FloutingUiMenu>
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
// export default withTheme(Menu);
// XNG-Changes
export default connect(null, mapDispatchToProps)(withTheme(Menu));

export { MenuItemText };
